/* dertbag 2026 — vanilla web components. Replaces jQuery, Handlebars, Modernizr, Magnific, Ajaxify. */

class NavDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-nav-toggle]');
    this.toggle?.addEventListener('click', () => this.setOpen(!this.hasAttribute('open')));
    this.onKey = (e) => { if (e.key === 'Escape') this.setOpen(false); };
    document.addEventListener('keydown', this.onKey);
  }
  disconnectedCallback() { document.removeEventListener('keydown', this.onKey); }
  setOpen(open) {
    this.toggleAttribute('open', open);
    this.toggle?.setAttribute('aria-expanded', String(open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
  }
}
customElements.define('nav-drawer', NavDrawer);

/* Variant picker — Section Rendering API, no page reload */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[type="application/json"]');
    if (!data) return;
    try { this.variants = JSON.parse(data.textContent); } catch (err) { return; }
    if (!Array.isArray(this.variants)) return;
    this.addEventListener('change', () => this.onChange());
    this.markAvailability();
  }
  /* Strike through any value that has no in-stock variant alongside the other
     values currently chosen. Left selectable so the button can say Sold out. */
  markAvailability() {
    const groups = [...this.querySelectorAll('.swatches')];
    const chosen = groups.map((g) => g.querySelector('input:checked')?.value);
    groups.forEach((g, pos) => {
      g.querySelectorAll('input').forEach((input) => {
        const ok = this.variants.some((v) => v.available && v.options[pos] === input.value &&
          v.options.every((o, k) => k === pos || chosen[k] === undefined || o === chosen[k]));
        input.classList.toggle('is-sold-out', !ok);
      });
    });
  }
  get selected() {
    const values = [...this.querySelectorAll('input:checked')].map((i) => i.value);
    return this.variants.find((v) => v.options.every((o, i) => o === values[i]));
  }
  onChange() {
    this.markAvailability();
    const variant = this.selected;
    const button = document.getElementById('AddToCart');
    if (!button) return;
    if (!variant) {
      button.disabled = true;
      button.textContent = button.dataset.unavailable;
      return;
    }
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    const idField = document.getElementById('VariantId');
    if (idField) idField.value = variant.id;
    const price = document.getElementById('Price');
    if (price) price.innerHTML = variant.price_html;
    button.disabled = !variant.available;
    button.textContent = variant.available ? button.dataset.add : button.dataset.soldOut;
    if (variant.featured_media) {
      document.querySelector('product-gallery')?.show(variant.featured_media.id);
    }
  }
}
customElements.define('variant-picker', VariantPicker);

/* Phone buy bar. Mirrors the main Add to cart button (label, disabled, Adding…)
   and price, and shows only while that button is out of view. Submits the
   product form through form="ProductForm", so ProductForm handles it as usual. */
class BuyBar extends HTMLElement {
  connectedCallback() {
    this.main = document.getElementById('AddToCart');
    this.btn = this.querySelector('[data-buybar-btn]');
    this.price = this.querySelector('[data-buybar-price]');
    const srcPrice = document.getElementById('Price');
    if (!this.main || !this.btn) return;
    document.body.classList.add('has-buybar');
    const sync = () => {
      this.btn.disabled = this.main.disabled;
      this.btn.textContent = this.main.textContent.trim();
      if (this.price && srcPrice) this.price.innerHTML = srcPrice.innerHTML;
    };
    sync();
    this.mo = new MutationObserver(sync);
    this.mo.observe(this.main, { attributes: true, childList: true, characterData: true, subtree: true });
    if (srcPrice) this.mo.observe(srcPrice, { childList: true, characterData: true, subtree: true });
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(([en]) => this.setVisible(!en.isIntersecting));
      this.io.observe(this.main);
    }
  }
  setVisible(on) {
    this.classList.toggle('is-visible', on);
    this.toggleAttribute('inert', !on);
  }
  disconnectedCallback() {
    this.mo?.disconnect();
    this.io?.disconnect();
    document.body.classList.remove('has-buybar');
  }
}
customElements.define('buy-bar', BuyBar);

/* Which sections to ask the Cart API to re-render — read from the page, never
   hardcoded, because a JSON template's section id is its template key. */
function cartSectionIds() {
  return [...document.querySelectorAll('[data-cart-section][data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

/* Add to cart. Reports Shopify's real error text (sold out, inventory limit,
   selling-plan mismatch) instead of silently re-enabling the button. */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.errorBox = this.querySelector('[data-form-error]');
    this.form.addEventListener('submit', (e) => this.submit(e));
  }
  async submit(e) {
    e.preventDefault();
    if (this.pending) return;
    this.pending = true;
    const label = this.button.textContent;
    this.button.disabled = true;
    this.button.textContent = this.button.dataset.adding || 'Adding…';
    this.setError('');

    const body = new FormData(this.form);
    body.append('sections', cartSectionIds());
    body.append('sections_url', window.location.pathname);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        this.setError(data.description || data.message || 'That variant could not be added.');
        return;
      }
      if (data.sections) applySections(data.sections);
      const drawer = document.querySelector('cart-drawer');
      if (drawer && !window.Shopify?.designMode) drawer.open();
      if (window.Shopify?.designMode) return;
      else window.location.href = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : '/cart';
    } catch (err) {
      this.setError('Network error — please try again.');
    } finally {
      this.pending = false;
      this.button.disabled = false;
      this.button.textContent = label;
    }
  }
  setError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = !message;
  }
}
customElements.define('product-form', ProductForm);

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (e.target.matches('.drawer__scrim') || e.target.closest('[data-drawer-close]')) this.close();
    });
    this.onKey = (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
      if (e.key === 'Tab' && this.hasAttribute('open')) this.trap(e);
    };
    document.addEventListener('keydown', this.onKey);
  }
  disconnectedCallback() { document.removeEventListener('keydown', this.onKey); }
  open() {
    this.lastFocused = document.activeElement;
    this.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    this.focusables()[0]?.focus();
  }
  close() {
    this.removeAttribute('open');
    document.documentElement.style.overflow = '';
    this.lastFocused?.focus();
  }
  focusables() {
    return [...this.querySelectorAll('a[href], button, input, select, textarea')]
      .filter((el) => !el.disabled && el.offsetParent !== null);
  }
  trap(e) {
    const items = this.focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* Cart lines. Debounced, sequenced (a stale response can't overwrite a newer
   one), and it surfaces the per-line error Shopify returns when a requested
   quantity exceeds stock. */
class CartItems extends HTMLElement {
  connectedCallback() {
    this.seq = 0;
    this.addEventListener('change', (e) => {
      if (!e.target.matches('[data-qty]')) return;
      /* An empty field is someone mid-edit, not a request to delete the line. */
      if (e.target.value.trim() === '') {
        e.target.value = e.target.defaultValue || 1;
        return;
      }
      this.queue(e.target.dataset.key, e.target.value);
    });
    this.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-remove]');
      if (remove) { e.preventDefault(); this.queue(remove.dataset.remove, 0); return; }
      const step = e.target.closest('[data-step]');
      if (step) {
        const input = this.querySelector('[data-qty][data-key="' + step.dataset.key + '"]');
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.step, 10));
        input.value = next;
        this.queue(step.dataset.key, next);
      }
    });
  }
  queue(key, quantity) {
    /* Per-line timers. A single shared timer meant editing a second line inside
       the debounce window cancelled the first line's pending update. */
    this.timers = this.timers || new Map();
    clearTimeout(this.timers.get(key));
    this.timers.set(key, setTimeout(() => {
      this.timers.delete(key);
      this.change(key, quantity);
    }, 250));
  }
  async change(key, quantity) {
    const mine = ++this.seq;
    this.setAttribute('aria-busy', 'true');
    this.lineError(key, '');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: Number(quantity),
          sections: cartSectionIds(),
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();
      if (mine !== this.seq) return; /* a newer request already went out */

      if (!res.ok || data.status) {
        this.lineError(key, data.description || data.message || 'Could not update that line.');
        return;
      }
      /* Shopify silently clamps to available stock — say so rather than snapping back */
      const line = data.items?.find((i) => i.key === key);
      if (line && Number(quantity) > 0 && line.quantity < Number(quantity)) {
        this.lineError(key, 'Only ' + line.quantity + ' available.');
      }
      if (data.sections) applySections(data.sections);
    } catch (err) {
      if (mine === this.seq) this.lineError(key, 'Network error — please try again.');
    } finally {
      if (mine === this.seq) this.removeAttribute('aria-busy');
    }
  }
  lineError(key, message) {
    const box = this.querySelector('[data-line-error="' + key + '"]');
    if (box) box.textContent = message;
  }
}
customElements.define('cart-items', CartItems);

/* Collection filtering + sorting without a reload */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', () => this.apply());
  }
  async apply() {
    const form = this.querySelector('form');
    const current = document.getElementById('ProductGrid');
    if (!form || !current) return;
    const params = new URLSearchParams(new FormData(form));
    const query = params.toString();
    const url = window.location.pathname + (query ? '?' + query : '');
    try {
      const res = await fetch(url + (query ? '&' : '?') + 'section_id=' + this.dataset.section);
      if (!res.ok) return;
      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = html.querySelector('#ProductGrid');
      if (fresh) current.replaceWith(fresh);
      window.history.replaceState({}, '', url);
    } catch (err) { /* leave the current grid in place */ }
  }
}
customElements.define('collection-filters', CollectionFilters);

function applySections(sections) {
  const activeKey = document.activeElement?.dataset?.key;
  const wasOpen = document.querySelector('cart-drawer')?.hasAttribute('open');

  Object.entries(sections).forEach(([id, html]) => {
    if (typeof html !== 'string') return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector('[data-section-id="' + id + '"]') || parsed.body.firstElementChild;
    const current = document.querySelector('[data-section-id="' + id + '"]');
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  });

  if (wasOpen) document.querySelector('cart-drawer')?.setAttribute('open', '');
  if (activeKey) {
    const restored = document.querySelector('[data-qty][data-key="' + activeKey + '"]');
    if (restored) { restored.focus(); restored.select?.(); }
  }

  const count = document.querySelector('[data-cart-count]')?.textContent;
  if (typeof announce === 'function' && count) announce('Cart updated. ' + count);
}

/* Hero slideshow: cross-fade, dots, arrows, keyboard, swipe, autoplay that pauses
   on hover, on focus, off-screen and for prefers-reduced-motion. */
class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (this.slides.length < 2) return;
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.captions = JSON.parse(this.querySelector('[data-captions]')?.textContent || '[]');
    this.left = this.querySelector('[data-caption-left]');
    this.right = this.querySelector('[data-caption-right]');
    this.index = 0;
    this.paused = false;

    /* the photograph itself advances; a linked slide keeps its link */
    this.querySelector('[data-advance]')?.addEventListener('click', (e) => {
      if (e.target.closest('a')) return;
      this.go(this.index + 1, true);
    });
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(+dot.dataset.dot, true)));

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
      if (e.key === 'ArrowRight') this.go(this.index + 1, true);
    });
    this.addEventListener('mouseenter', () => { this.paused = true; });
    this.addEventListener('mouseleave', () => { this.paused = false; });
    this.addEventListener('focusin', () => { this.paused = true; });
    this.addEventListener('focusout', () => { this.paused = false; });

    let startX = null;
    this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
      startX = null;
    }, { passive: true });

    this.go(0);

    const ms = parseInt(this.getAttribute('autoplay') || '0', 10);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (ms && !still) {
      const io = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; });
      io.observe(this);
      this.timer = setInterval(() => {
        if (!this.paused && this.visible !== false && !document.hidden) this.go(this.index + 1);
      }, ms);
    }
  }
  disconnectedCallback() { clearInterval(this.timer); }
  go(next, userDriven) {
    const total = this.slides.length;
    this.index = (next + total) % total;
    this.slides.forEach((slide, i) => {
      const on = i === this.index;
      slide.classList.toggle('is-active', on);
      slide.toggleAttribute('aria-hidden', !on);
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    const caption = this.captions[this.index];
    if (caption) {
      if (this.left) this.left.textContent = caption.left || '';
      if (this.right) this.right.textContent = caption.right || '';
    }
    if (userDriven) this.paused = false;
  }
}
customElements.define('hero-slideshow', HeroSlideshow);

/* Product gallery. Every photo is on the page; any one opens the lightbox there.
   Lightbox: shows the already-loaded image instantly, swaps in the 2400px file
   once it has decoded, preloads both neighbours, and supports arrows, keys,
   swipe and click-to-zoom (pans with the pointer, native scroll on touch). */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-index]')];
    this.track = this.querySelector('[data-track]');
    this.counter = this.querySelector('[data-count]');
    this.items.forEach((el, i) => el.addEventListener('click', () => this.open(i)));

    if (this.counter && 'IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) this.counter.textContent = Number(en.target.dataset.index) + 1;
        });
      }, { threshold: 0.6 });
      this.items.forEach((el) => this.io.observe(el));
    }
  }
  disconnectedCallback() {
    this.io?.disconnect();
    if (this.dialog?.open) this.dialog.close();
    this.dialog?.remove();
  }

  /* Variant change: bring that variant's photo into view. */
  show(mediaId) {
    const el = this.items.find((i) => i.dataset.mediaId == mediaId);
    if (!el || !this.track) return;
    if (this.track.scrollWidth > this.track.clientWidth + 1) {
      this.track.scrollTo({ left: el.offsetLeft, behavior: 'smooth' });
    } else {
      const top = el.getBoundingClientRect().top + window.scrollY - 24;
      if (Math.abs(el.getBoundingClientRect().top) > 40) window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  build() {
    const d = document.createElement('dialog');
    d.className = 'lightbox';
    d.setAttribute('aria-label', 'Product photos');
    d.innerHTML =
      '<div class="lightbox__bar"><span data-lb-count></span>' +
      '<button type="button" class="lightbox__close" data-lb-close>Close</button></div>' +
      '<div class="lightbox__stage" data-lb-stage><img alt="" data-lb-img>' +
      '<button type="button" class="lightbox__nav lightbox__nav--prev" data-lb-prev aria-label="Previous photo">Prev</button>' +
      '<button type="button" class="lightbox__nav lightbox__nav--next" data-lb-next aria-label="Next photo">Next</button></div>';
    document.body.append(d);
    this.dialog = d;
    this.stage = d.querySelector('[data-lb-stage]');
    this.img = d.querySelector('[data-lb-img]');
    this.countEl = d.querySelector('[data-lb-count]');
    d.classList.toggle('is-single', this.items.length < 2);

    d.querySelector('[data-lb-close]').addEventListener('click', () => d.close());
    d.querySelector('[data-lb-prev]').addEventListener('click', (e) => { e.stopPropagation(); this.go(this.index - 1); });
    d.querySelector('[data-lb-next]').addEventListener('click', (e) => { e.stopPropagation(); this.go(this.index + 1); });
    d.addEventListener('close', () => { this.unzoom(); document.documentElement.style.overflow = ''; });
    d.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.go(this.index + 1);
      if (e.key === 'ArrowLeft') this.go(this.index - 1);
    });

    /* empty stage closes; the photo zooms */
    this.stage.addEventListener('click', (e) => {
      if (this.moved) { this.moved = false; return; }
      if (e.target === this.img) this.toggleZoom(e);
      else if (e.target === this.stage && !this.zoomed) d.close();
    });

    /* swipe between photos when not zoomed */
    let x0 = null;
    this.stage.addEventListener('pointerdown', (e) => { x0 = this.zoomed ? null : e.clientX; this.moved = false; });
    this.stage.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 50) { this.moved = true; this.go(this.index + (dx < 0 ? 1 : -1)); }
    });

    /* mouse pans a zoomed photo; touch scrolls it natively */
    this.stage.addEventListener('mousemove', (e) => {
      if (!this.zoomed || !window.matchMedia('(hover: hover)').matches) return;
      const r = this.stage.getBoundingClientRect();
      this.stage.scrollLeft = ((e.clientX - r.left) / r.width) * (this.stage.scrollWidth - this.stage.clientWidth);
      this.stage.scrollTop = ((e.clientY - r.top) / r.height) * (this.stage.scrollHeight - this.stage.clientHeight);
    });
  }

  open(i) {
    if (!this.items.length) return;
    if (!this.dialog) this.build();
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    this.go(i);
  }

  go(i) {
    const n = this.items.length;
    this.index = ((i % n) + n) % n;
    this.unzoom();
    const item = this.items[this.index];
    const shown = item.querySelector('img');

    /* instant: the copy already on the page */
    if (shown) { this.img.src = shown.currentSrc || shown.src; this.img.alt = shown.alt; }
    this.countEl.textContent = n > 1 ? (this.index + 1) + ' / ' + n : '';

    /* then the full-resolution file, only if we are still on this photo */
    const want = this.index;
    const full = new Image();
    full.src = item.dataset.full;
    this.img.classList.add('is-loading');
    (full.decode ? full.decode() : Promise.resolve())
      .then(() => { if (this.index === want) this.img.src = full.src; })
      .catch(() => {})
      .finally(() => { if (this.index === want) this.img.classList.remove('is-loading'); });

    [this.index - 1, this.index + 1].forEach((k) => {
      const nb = this.items[((k % n) + n) % n];
      if (nb && nb !== item) new Image().src = nb.dataset.full;
    });
  }

  toggleZoom(e) {
    if (this.zoomed) { this.unzoom(); return; }
    const item = this.items[this.index];
    const rect = this.img.getBoundingClientRect();
    const target = Math.min(Number(item.dataset.w) || this.img.naturalWidth, 2400, rect.width * 3);
    if (target <= rect.width * 1.1) return;
    const rx = (e.clientX - rect.left) / rect.width;
    const ry = (e.clientY - rect.top) / rect.height;
    this.zoomed = true;
    this.stage.classList.add('is-zoomed');
    this.dialog.classList.add('is-zoomed');
    this.img.style.width = target + 'px';
    this.stage.scrollLeft = rx * this.img.offsetWidth - this.stage.clientWidth / 2;
    this.stage.scrollTop = ry * this.img.offsetHeight - this.stage.clientHeight / 2;
  }

  unzoom() {
    if (!this.stage) return;
    this.zoomed = false;
    this.stage.classList.remove('is-zoomed');
    this.dialog.classList.remove('is-zoomed');
    this.img.style.width = '';
    this.stage.scrollTop = 0;
    this.stage.scrollLeft = 0;
  }
}
customElements.define('product-gallery', ProductGallery);

/* Discount code. /discount/CODE?redirect= is the only supported storefront way
   to apply a code before checkout; the cart API has no endpoint for it. */
class DiscountForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="code"]');
    /* A nested <form> is dropped by the parser, so guard rather than throwing
       during upgrade and taking the rest of this element's wiring with it. */
    if (!this.form || !this.input) return;
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      const code = this.input.value.trim();
      if (!code) return;
      const back = this.dataset.redirect || window.location.pathname;
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(back);
    });
  }
}
customElements.define('discount-form', DiscountForm);

/* Newsletter. Posts straight to Mailchimp through a hidden iframe, so the
   customer never leaves the page and we need no API key. Mailchimp's endpoint
   blocks cross-origin fetch, which is why this is a form target, not a fetch. */
class NewsletterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="email"]');
    this.done = this.querySelector('[data-newsletter-done]');
    if (!this.form || !this.input) return;
    this.form.addEventListener('submit', () => {
      if (!this.input.value.includes('@')) return;
      setTimeout(() => {
        this.form.hidden = true;
        if (this.done) this.done.hidden = false;
      }, 250);
    });
  }
}
customElements.define('newsletter-form', NewsletterForm);

/* Theme editor: Shopify re-renders a section on every setting change. Clear any
   interval the removed instance owned, or timers stack up and the slideshow
   accelerates as you edit. */
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('hero-slideshow').forEach((el) => clearInterval(el.timer));
});

/* Announce cart changes to screen readers — the drawer updates silently otherwise. */
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('CartLive')) return;
  const live = document.createElement('div');
  live.id = 'CartLive';
  live.className = 'visually-hidden';
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('role', 'status');
  document.body.append(live);
});
function announce(message) {
  const live = document.getElementById('CartLive');
  if (live) live.textContent = message;
}
